/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Pagination,
    Stack,
    Box,
    Text,
    Inline,
    Button,
    Icon,
    pluralize,
    STATUS,
} from 'sarsaparilla';

import { tourImageMapType, tourRatingsMapType, toursType } from '../../types';

import { clearFilters } from '../../actions/filtersActions';

import { TourList } from './TourList';

export function PaginatedTourList({
    tours = [],
    totalTourCount,
    tourImageMap,
    tourRatingsMap,
    cardsPerPage = 10,
    onCardOverEvent,
}) {
    const dispatch = useDispatch();
    const isFilterModalOpen = useSelector(
        (state) => state.timedEntry.filters.isFilterModalOpen
    );
    const filterData = useSelector((state) => state.timedEntry.filters);
    const availableTours =
        useSelector((state) => state.timedEntry.filters.filteredTourIds) || [];
    const ticketTimesStore = useSelector((state) => state.timedEntry.tourTimes);
    const firstComeFirstServedToursByDate = useSelector(
        (state) => state.timedEntry.facilityAvailability.firstComeFirstServedToursByDate
    );
    const { farthestByTour } = useSelector(
        (state) => state.timedEntry.tourAvailability.farthestAvailability
    );

    const containerRef = useRef();
    const prevActivePageRef = useRef(0);
    const [activePage, setActivePage] = useState(0);
    const [toursOnCurrentPage, setToursOnCurrentPage] = useState([]);
    const cardCount = tours?.length || 0;
    const pageCount = Math.ceil(cardCount / cardsPerPage);
    const initialRender = useRef(true);

    const { date: filterDate } = filterData;

    const filteredTourTimes = ticketTimesStore.filteredTourTimes;
    const areTimesLoading = ticketTimesStore.loadingStatus === STATUS.PENDING;
    const hasTimesLoadingError = ticketTimesStore.loadingStatus === STATUS.ERROR;

    const filteredFirstComeFirstServedTours = useMemo(() => {
        let firstComeFirstServedTours = {};

        if (filterDate) {
            const date = filterDate?.format('YYYY-MM-DD');
            const monthKey = date?.slice(0, -3);
            firstComeFirstServedTours =
                firstComeFirstServedToursByDate?.[monthKey]?.[date] ?? {};
        }

        return firstComeFirstServedTours;
    }, [filterDate, firstComeFirstServedToursByDate]);

    const handleSetActivePage = useCallback(
        (page) => {
            prevActivePageRef.current = activePage;
            setActivePage(page);
        },
        [activePage]
    );

    useEffect(() => {
        if (pageCount === 1) {
            handleSetActivePage(0);
        }
    }, [handleSetActivePage, pageCount]);

    useEffect(() => {
        let isMounted = true;

        if (initialRender.current) {
            initialRender.current = false;
        } else if (
            prevActivePageRef.current !== activePage &&
            isMounted &&
            !isFilterModalOpen
        ) {
            setTimeout(() => {
                containerRef.current.focus();
                prevActivePageRef.current = activePage;
            }, 0);
        }

        const offset = activePage * cardsPerPage;
        const slicedTours = tours.slice(offset, offset + cardsPerPage);

        setToursOnCurrentPage(slicedTours);

        return () => {
            isMounted = false;
        };
    }, [tours, activePage, cardsPerPage, isFilterModalOpen]);

    const areToursFiltered = tours.length < totalTourCount;

    return (
        <>
            <div
                ref={containerRef}
                tabIndex="-1"
                style={{ outline: 'none' }}
                role="navigation"
                aria-label={`Tour list page ${activePage + 1} of ${pageCount}`}
            />
            <Stack>
                <TourList
                    availableTours={availableTours}
                    ticketTimesById={filteredTourTimes}
                    areTimesLoading={areTimesLoading}
                    hasTimesLoadingError={hasTimesLoadingError}
                    tours={toursOnCurrentPage}
                    tourImageMap={tourImageMap}
                    tourRatingsMap={tourRatingsMap}
                    filterDate={filterDate}
                    firstComeFirstServedTours={filteredFirstComeFirstServedTours}
                    onCardOverEvent={onCardOverEvent}
                    farthestByTour={farthestByTour}
                />

                {areToursFiltered && (
                    <Inline>
                        <Text size="sm">
                            {`Showing ${tours.length} of ${totalTourCount} ${pluralize(
                                totalTourCount,
                                'ticket',
                                'tickets'
                            )}`}
                        </Text>
                        <Button
                            iconBeforeElement={<Icon iconName="close" />}
                            appearance="link"
                            size="sm"
                            onClick={() => {
                                dispatch(clearFilters());
                            }}
                        >
                            Clear filters
                        </Button>
                    </Inline>
                )}

                {pageCount > 1 && (
                    <Box alignX="center">
                        <Pagination
                            pages={pageCount}
                            activePage={activePage}
                            onChange={handleSetActivePage}
                        />
                    </Box>
                )}
            </Stack>
        </>
    );
}

PaginatedTourList.propTypes = {
    tours: toursType,
    tourImageMap: tourImageMapType,
    tourRatingsMap: tourRatingsMapType,
    cardsPerPage: PropTypes.number,
    totalTourCount: PropTypes.number,
    onCardOverEvent: PropTypes.func,
};
